import {
  useEffect,
  useState,
} from 'react';

import {
  Calendar,
  Download,
  Filter,
  LogOut,
  Package,
  Ship,
  TrendingUp,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { ordersAPI } from '../services/api';

export const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [vesselFilter, setVesselFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadOrders();
  }, [user]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ordersAPI.getAll();
      setOrders(data);
    } catch (err) {
      console.error('Failed to load orders:', err);
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    filterOrders();
  }, [orders, vesselFilter, fromDate, toDate]);

  const filterOrders = () => {
    let filtered = [...orders];

    if (vesselFilter !== 'all') {
      filtered = filtered.filter(o => o.vessel === vesselFilter);
    }

    if (fromDate) {
      filtered = filtered.filter(o => new Date(o.etd) >= new Date(fromDate));
    }

    if (toDate) {
      const to = new Date(toDate);
      to.setDate(to.getDate() + 1);
      filtered = filtered.filter(o => new Date(o.etd) < to);
    }

    setFilteredOrders(filtered);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const vessels = [...new Set(orders.map(o => o.vessel))].sort();
  const totalPallets = filteredOrders.reduce((sum, o) => sum + (o.pallets || 0), 0);
  const uniqueVessels = new Set(filteredOrders.map(o => o.vessel)).size;

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'TBC') return 'TBC';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const exportToCSV = () => {
    const headers = ['Week', 'Client', 'Product', 'Variety', 'Pallets', 'Load Date', 'Vessel', 'ETD', 'ETA', 'Status'];
    const rows = filteredOrders.map(o => [
      o.week, o.client, o.product, o.variety, o.pallets, o.loadDate, o.vessel, o.etd, o.eta, o.status
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(f => `"${f}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'citrus-orders.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="brand">
          <div className="brand-logo">EG</div>
          <div>
            <h1>Egyptian Citrus Season</h1>
            <p>Welcome, {user.name} {user.role === 'admin' && '(Admin)'}</p>
          </div>
        </div>
        <div className="header-actions">
          {user.role === 'admin' && (
            <button onClick={() => navigate('/admin')} className="btn-secondary">
              Admin Panel
            </button>
          )}
          <button onClick={handleLogout} className="btn-secondary">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="summary-cards">
          <div className="summary-card">
            <Package className="card-icon" />
            <div className="card-content">
              <div className="card-label">Total Pallets</div>
              <div className="card-value">{totalPallets}</div>
            </div>
          </div>
          <div className="summary-card">
            <Ship className="card-icon" />
            <div className="card-content">
              <div className="card-label">Vessels</div>
              <div className="card-value">{uniqueVessels}</div>
            </div>
          </div>
          <div className="summary-card">
            <TrendingUp className="card-icon" />
            <div className="card-content">
              <div className="card-label">Shipments</div>
              <div className="card-value">{filteredOrders.length}</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2>Order Overview</h2>
            <button onClick={exportToCSV} className="btn-secondary">
              <Download size={18} />
              Export CSV
            </button>
          </div>

          <div className="filters">
            <div className="filter-group">
              <label>
                <Ship size={16} />
                Vessel
              </label>
              <select value={vesselFilter} onChange={(e) => setVesselFilter(e.target.value)}>
                <option value="all">All vessels</option>
                {vessels.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>

            <div className="filter-group">
              <label>
                <Calendar size={16} />
                ETD From
              </label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>

            <div className="filter-group">
              <label>
                <Calendar size={16} />
                ETD To
              </label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>

            <button onClick={() => { setVesselFilter('all'); setFromDate(''); setToDate(''); }} className="btn-secondary">
              <Filter size={16} />
              Reset
            </button>
          </div>

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Week</th>
                  <th>Client</th>
                  <th>Product</th>
                  <th>Variety</th>
                  <th>Pallets</th>
                  <th>Load Date</th>
                  <th>Vessel</th>
                  <th>ETD</th>
                  <th>ETA</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '2rem' }}>
                      No orders found. {user.role === 'admin' && 'Add orders from the Admin Panel.'}
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order, idx) => (
                    <tr key={order.id || idx}>
                      <td><span className="badge">{order.week}</span></td>
                      <td>{order.client}</td>
                      <td>{order.product}</td>
                      <td>{order.variety}</td>
                      <td className="highlight">{order.pallets}</td>
                      <td>{formatDate(order.loadDate)}</td>
                      <td>{order.vessel}</td>
                      <td>{formatDate(order.etd)}</td>
                      <td>{formatDate(order.eta)}</td>
                      <td>
                        <span className={`status-badge status-${order.status.toLowerCase()}`}>
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};
